"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AllVendorPayments = () => {
    const [payments, setPayments] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [vendorList, setVendorList] = useState([]);
    const [vendorsLoading, setVendorsLoading] = useState(false);

    const [newPayment, setNewPayment] = useState({
        vendor_email: "",
        payment_amount: "",
        payment_date: "",
        payment_mode: "online",
        transaction_id: "",
        transaction_details: "",
    });
    const formatToApiDate = (dateString) => {
        if (!dateString) return "";

        const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

        const dateObj = new Date(dateString);
        const day = dateObj.getDate().toString().padStart(2, "0");
        const month = months[dateObj.getMonth()];
        const year = dateObj.getFullYear();

        return `${day}-${month}-${year}`;
    };


    useEffect(() => {
        fetchPayments();
        fetchVendors();
    }, []);


    const fetchVendors = async () => {
        try {
            setVendorsLoading(true);
            const res = await axios.get(
                "https://logicx.premierlam.com/api/pixel-get-all-vendor-email-name",
                { headers: { authorization: localStorage.getItem("accessToken") } }
            );

            setVendorList(res.data.data || []);
        } catch (err) {
            console.log("Vendor list load failed:", err);
        } finally {
            setVendorsLoading(false);
        }
    };
    const fetchPayments = async () => {
        try {
            setIsLoading(true);
            const res = await axios.get(
                `https://logicx.premierlam.com/api/get-pixel-all-vendor-payments-for-admin`,
                {
                    headers: { authorization: localStorage.getItem("accessToken") },
                }
            );
            setPayments(res.data || []);
        } catch (err) {
            console.log(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddPayment = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);

            await axios.post(
                `https://logicx.premierlam.com/api/pixel-add-vendor-payments`,
                newPayment,
                {
                    headers: {
                        authorization: localStorage.getItem("accessToken"),
                        "Content-Type": "application/json",
                    },
                }
            );

            await fetchPayments();
            setNewPayment({
                vendor_email: "",
                payment_amount: "",
                payment_date: "",
                payment_mode: "online",
                transaction_id: "",
                transaction_details: "",
            });

            setIsAddDialogOpen(false);
            toast.success("Payment added successfully!");
        } catch (err) {
            console.log(err);
            toast.error(err.response?.data?.message || "Failed to add payment");

        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    };

    const filteredPayments = payments.filter((p) =>
        p.vendor_email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />


            <div className="min-h-screen bg-gray-50 p-2">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900 mb-1">
                                Vendor Payments
                            </h1>
                            <p className="text-gray-500">
                                Manage all vendor payment entries
                            </p>
                        </div>

                        <button
                            onClick={() => setIsAddDialogOpen(true)}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                        >
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                />
                            </svg>
                            Add Payment
                        </button>
                    </div>

                    {/* Table Container */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        {/* Search */}
                        <div className="relative mb-6">
                            <svg
                                className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"
                                />
                            </svg>

                            <input
                                type="text"
                                placeholder="Search vendor email"
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none w-64"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto">
                            {isLoading ? (
                                <div className="flex justify-center py-6">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                                </div>
                            ) : (
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-200">
                                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                                                Vendor Email
                                            </th>
                                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                                                Vendor Name
                                            </th>
                                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                                                Amount
                                            </th>
                                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                                                Mode
                                            </th>
                                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                                                Transaction ID
                                            </th>
                                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                                                Payment Date
                                            </th>

                                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                                                Added By
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {filteredPayments.map((p) => (
                                            <tr
                                                key={p.id}
                                                className="border-b border-gray-100 hover:bg-gray-50"
                                            >
                                                <td className="py-3 px-4 text-sm text-gray-900">
                                                    {p.vendor_email}
                                                </td>
                                                <td className="py-3 px-4 text-sm text-gray-900">
                                                    {p.vendor_name}
                                                </td>
                                                <td className="py-3 px-4 text-sm text-gray-900">
                                                    ₹{p.payment_amount}
                                                </td>
                                                <td className="py-3 px-4 text-sm text-gray-600">
                                                    {p.payment_mode}
                                                </td>
                                                <td className="py-3 px-4 text-sm text-gray-600">
                                                    {p.transaction_id}
                                                </td>
                                                <td className="py-3 px-4 text-sm text-gray-600">
                                                    {p.payment_date}
                                                </td>
                                                <td className="py-3 px-4 text-sm text-gray-600">
                                                    {p.added_by}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    {/* Add Payment Modal */}
                    {isAddDialogOpen && (
                        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                            <div className="bg-white p-6 rounded-lg w-full max-w-md relative">
                                <button
                                    className="absolute right-3 top-2 text-gray-400 hover:text-gray-700"
                                    onClick={() => setIsAddDialogOpen(false)}
                                >
                                    ✖
                                </button>

                                <h2 className="text-lg font-semibold mb-4">
                                    Add Vendor Payment
                                </h2>

                                <form onSubmit={handleAddPayment} className="space-y-3">
                                    <select
                                        className="w-full border rounded px-3 py-2"
                                        value={newPayment.vendor_email}
                                        onChange={(e) =>
                                            setNewPayment({ ...newPayment, vendor_email: e.target.value })
                                        }
                                        required
                                    >
                                        <option value="">Select Vendor</option>

                                        {vendorsLoading && <option>Loading...</option>}

                                        {vendorList.map((v) => (
                                            <option key={v.email} value={v.email}>
                                                {v.full_name} ({v.email})
                                            </option>
                                        ))}
                                    </select>



                                    <input
                                        type="number"
                                        placeholder="Payment Amount"
                                        className="w-full border rounded px-3 py-2"
                                        value={newPayment.payment_amount}
                                        onChange={(e) =>
                                            setNewPayment({
                                                ...newPayment,
                                                payment_amount: e.target.value,
                                            })
                                        }
                                        required
                                    />

                                    <input
                                        type="date"
                                        className="w-full border rounded px-3 py-2"
                                        onChange={(e) => {
                                            const rawDate = e.target.value;       // yyyy-mm-dd
                                            const formatted = formatToApiDate(rawDate);  // convert to 20-nov-2023
                                            setNewPayment({ ...newPayment, payment_date: formatted });
                                        }}
                                        required
                                    />


                                    <select
                                        className="w-full border rounded px-3 py-2"
                                        value={newPayment.payment_mode}
                                        onChange={(e) =>
                                            setNewPayment({
                                                ...newPayment,
                                                payment_mode: e.target.value,
                                            })
                                        }
                                    >
                                        <option value="online">Online</option>
                                        <option value="cash">Cash</option>
                                    </select>

                                    <input
                                        type="text"
                                        placeholder="Transaction ID"
                                        className="w-full border rounded px-3 py-2"
                                        value={newPayment.transaction_id}
                                        onChange={(e) =>
                                            setNewPayment({
                                                ...newPayment,
                                                transaction_id: e.target.value,
                                            })
                                        }
                                    />

                                    <input
                                        type="text"
                                        placeholder="Transaction Details"
                                        className="w-full border rounded px-3 py-2"
                                        value={newPayment.transaction_details}
                                        onChange={(e) =>
                                            setNewPayment({
                                                ...newPayment,
                                                transaction_details: e.target.value,
                                            })
                                        }
                                    />

                                    <div className="flex justify-end gap-3 mt-4">
                                        <button
                                            type="button"
                                            onClick={() => setIsAddDialogOpen(false)}
                                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                                        >
                                            Cancel
                                        </button>

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
                                        >
                                            {isSubmitting ? "Adding..." : "Add Payment"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </>
    );
};

export default AllVendorPayments;
